/**
 * TrendPortfolioTable — Resident Trends portfolio rollup for corporate users.
 * One row per trend-enabled community: open concerns, flag rate, 30-day
 * activity, time-to-acknowledge, and 90-day outcome/recurrence rates.
 *
 * Self-fetching. Props: corpId
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchTrendPortfolio } from '../api/corpDashApi.js';

const num = (v) => (v == null ? 0 : Number(v) || 0);

const pct = (part, whole) => {
    const w = num(whole);
    return w > 0 ? Math.round((num(part) / w) * 100) : null;
};

const Cell = ({ children, align = 'right', muted = false }) => (
    <td style={{ padding: '8px 10px', textAlign: align, fontSize: 'var(--font-size-xs)', color: muted ? 'var(--color-text-muted)' : 'var(--color-text-body)', whiteSpace: 'nowrap' }}>
        {children}
    </td>
);

const Th = ({ children, align = 'right' }) => (
    <th style={{ padding: '8px 10px', textAlign: align, fontSize: 10, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap', borderBottom: '1px solid var(--color-border)' }}>
        {children}
    </th>
);

const RateBadge = ({ value, goodHigh = true }) => {
    if (value == null) return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
    const good = goodHigh ? value >= 50 : value <= 20;
    const color = good ? 'var(--color-trend-good)' : value === 0 && goodHigh ? 'var(--color-text-muted)' : 'var(--color-trend-warn)';
    return <span style={{ fontWeight: 600, color }}>{value}%</span>;
};

const TrendPortfolioTable = ({ corpId }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        setLoading(true);
        const result = await fetchTrendPortfolio(corpId);
        setRows(Array.isArray(result?.campuses) ? result.campuses : []);
        setLoading(false);
    }, [corpId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    if (loading) {
        return <div style={{ padding: 'var(--space-8) 0', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>Loading portfolio...</div>;
    }
    if (rows.length === 0) {
        return (
            <div style={{ padding: 'var(--space-8) 0', textAlign: 'center' }}>
                <div style={{ color: 'var(--color-text-heading)', fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>No communities with Resident Trends enabled</div>
                <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)', marginTop: 4 }}>Enable the feature on a community to see it here</div>
            </div>
        );
    }

    return (
        <div className="md-section" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--color-text-heading)' }}>Resident Trends Portfolio</span>
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                    Activity over 30 days · outcomes over 90 days
                </span>
            </div>
            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <Th align="left">Community</Th>
                            <Th>Open</Th>
                            <Th>High</Th>
                            <Th>Flag Rate</Th>
                            <Th>Opened 30d</Th>
                            <Th>Resolved 30d</Th>
                            <Th>Avg Hrs to Ack</Th>
                            <Th>Improved 90d</Th>
                            <Th>Recurred 90d</Th>
                            <Th>Alerts</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => {
                            const outcomes = num(row.improved_90d) + num(row.unchanged_90d) + num(row.worsened_90d) + num(row.recurred_90d);
                            const flagRate = pct(row.open_episodes, row.census);
                            return (
                                <tr key={row.campus_id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                                    <Cell align="left"><span style={{ fontWeight: 600, color: 'var(--color-text-heading)' }}>{row.campus_name || `Campus ${row.campus_id}`}</span></Cell>
                                    <Cell>{num(row.open_episodes)}</Cell>
                                    <Cell>{num(row.high_priority) > 0 ? <span style={{ color: 'var(--color-trend-bad)', fontWeight: 600 }}>{num(row.high_priority)}</span> : 0}</Cell>
                                    <Cell>
                                        {flagRate == null ? '—' : (
                                            <span style={{ fontWeight: 600, color: flagRate > 25 ? 'var(--color-trend-bad)' : flagRate > 20 ? 'var(--color-trend-warn)' : 'var(--color-text-body)' }}>
                                                {flagRate}%
                                            </span>
                                        )}
                                    </Cell>
                                    <Cell>{num(row.opened_30d)}</Cell>
                                    <Cell>{num(row.resolved_30d)}</Cell>
                                    <Cell muted={row.avg_hours_to_ack == null}>{row.avg_hours_to_ack != null ? Number(row.avg_hours_to_ack).toFixed(1) : '—'}</Cell>
                                    <Cell><RateBadge value={pct(row.improved_90d, outcomes)} goodHigh /></Cell>
                                    <Cell><RateBadge value={pct(row.recurred_90d, outcomes)} goodHigh={false} /></Cell>
                                    <Cell>{num(row.open_insights) > 0 ? <span style={{ color: 'var(--color-trend-warn)', fontWeight: 600 }}>{num(row.open_insights)} community</span> : <span style={{ color: 'var(--color-text-muted)' }}>—</span>}</Cell>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <div style={{ padding: '8px 20px 12px', fontSize: 10, color: 'var(--color-text-muted)' }}>
                Flag rate = residents with an open concern / census (target 10-20%). Improved / recurred rates are shares of episodes with a stamped outcome in the last 90 days.
            </div>
        </div>
    );
};

export default TrendPortfolioTable;
